import {
  CustomFieldProps,
  DynamicField,
  FieldVariant,
  NumberFieldProps,
  SelectFieldProps,
  SelectOption,
  SwitchFieldProps,
  TextFieldProps,
} from '@/components/form-builder/form-builder.types';
import { MultiFormAccordionComponent } from '@/components/multi-form-accordion/multi-form-accordion.component';
import { DynamicFormRepository } from '@/store/dynamic-form-state/dynamic-form.repository';
import { DynaStatus, FormType } from '@/types';
import { capitalize } from 'lodash';
import { DynamicFieldFormComponent } from '../../dynamic-field-form/dynamic-field-form.component';
import { emptyFieldObjectFactory } from '@/components/form-builder/utils/single-field-factory';
import { map, Observable } from 'rxjs';

interface getNewDynamicFormFormObjectProps {
  store: DynamicFormRepository;
  resetFormType?: () => void;
  folderTypeOptions?: Observable<SelectOption[]>;
}

export const getNewDynamicFormFormObject = ({ store, resetFormType, folderTypeOptions }: getNewDynamicFormFormObjectProps) => {
  const nameField: DynamicField<TextFieldProps> = {
    label: 'transanctionalForm.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'name'),
    props: {
      value: store.getNestedObservable<string>('dto', 'name'),
      onChange: (value: string) => {
        store.setNested('dto', 'name', value);
        store.clearError('name');
      },
      placeholder: 'transanctionalForm.form.placeholders.name',
    },
  };

  const folderTypeField: DynamicField<SelectFieldProps> = {
    label: 'transanctionalForm.form.typeDossier',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'typeDossier'),
    props: {
      value: store.getObservable('typeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('typeSelected', value);
        store.setNested('dto', 'typeDossierId', value?.code);
        store.clearError('typeDossierId');
      },
      placeholder: 'transanctionalForm.form.placeholders.typeDossier',
      options: folderTypeOptions,
    },
  };

  const statusField: DynamicField<SwitchFieldProps> = {
    label: 'transanctionalForm.form.status',
    variant: FieldVariant.SWITCH,
    props: {
      checked: store.getNestedObservable<DynaStatus>('dto', 'status').pipe(map(value => value === DynaStatus.ACTIF)),
      onChange: value => {
        store.setNested('dto', 'status', value.checked ? DynaStatus.ACTIF : DynaStatus.NON_ACTIF);
        store.clearError('status');
      },
      placeholder: 'transanctionalForm.form.placeholders.status',
    },
  };

  const formTypeField: DynamicField<SelectFieldProps> = {
    label: 'transanctionalForm.form.formType',
    variant: FieldVariant.SELECT,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'formType'),
    props: {
      value: store.getObservable('formTypeSelected'),
      onSelectChange: (value: SelectOption) => {
        store.set('formTypeSelected', value);
        store.setNested('dto', 'formType', value?.code);
        resetFormType?.();
        store.clearError('formType');
      },
      placeholder: 'transanctionalForm.form.placeholders.formType',
      options: Object.entries(FormType).map(([key, value]) => ({
        code: FormType[key as keyof typeof FormType],
        name: `transanctionalForm.form.formType${capitalize(value.toString())}`,
      })),
    },
  };

  const orderField: DynamicField<NumberFieldProps> = {
    label: 'transanctionalForm.form.order',
    variant: FieldVariant.NUMBER,
    isRequired: true,
    error: store.getNestedObservable<string>('errors', 'order'),
    props: {
      value: store.getNestedObservable<number>('dto', 'order'),
      onChange: (value: number) => {
        store.setNested('dto', 'order', value);
        store.clearError('order');
      },
      placeholder: 'transanctionalForm.form.placeholders.order',
    },
  };

  const fieldsField: DynamicField<CustomFieldProps> = {
    label: 'transanctionalForm.form.fields',
    variant: FieldVariant.CUSTOM,
    isRequired: true,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: ['transanctionalForm.dynamicField.title'],
        component: DynamicFieldFormComponent,
        injectKeys: ['isDisabled', 'formType'],
        isDisabled: false,
        formType: store.getNestedObservable<FormType>('dto', 'formType'),
      },
    },
  };

  const formObject = {
    title: 'Formulaire dynamique',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [nameField, orderField],
          },
          {
            fields: [folderTypeField, formTypeField],
          },
          {
            fields: [statusField, emptyFieldObjectFactory()],
          },
          {
            fields: [fieldsField],
          },
        ],
      },
    ],
  };

  return formObject;
};
